import * as Sentry from "@sentry/react";
import { telemetry } from "@/lib/telemetry";
import apiClient from "./client";
import type { components } from "@/types/api.generated";
import type {
  AgentDefinition,
  AgentStreamRequest,
  AgentChatRequest,
  AgentResumeRequest,
  AgentExecution,
  PreparedInputsResponse,
  StreamEventType,
  ChatMessage,
  CostSummary,
  ExecutionMetrics,
} from "@/types/api";

/**
 * Contrato das rotas de Runs (#867), derivado do tipo gerado.
 *
 * Estes tipos estavam escritos à mão em `types/api.ts`, cujo cabeçalho pede
 * justamente o contrário — e a cópia já divergia: restringia `status` a cinco
 * valores enquanto o backend grava `str`. Um status novo passava pelo
 * TypeScript sem aviso e a tela o chamava de "Waiting".
 *
 * **Um campo só é estreitado, e de propósito: `metrics`.** O backend o declara
 * `Dict[str, Any]`, então o gerado diz `{[key: string]: unknown}`. O formato
 * real é o `ExecutionMetrics` que o serviço de agents grava — o mesmo que o
 * resto do app já usa para ler execução. Todo o resto vem do gerado e não tem
 * como divergir.
 */
export type ProjectRun = Omit<components["schemas"]["ProjectRun"], "metrics"> & {
  metrics?: ExecutionMetrics | null;
};
export type ProjectRunsResponse = Omit<
  components["schemas"]["ProjectRunsResponse"],
  "items"
> & {
  items: ProjectRun[];
};
export type ProjectRunStats = components["schemas"]["ProjectRunStats"];

export async function getAgentSchema(
  projectId: string,
  agentId: string,
): Promise<AgentDefinition> {
  const res = await apiClient.get<AgentDefinition>(
    `/api/projects/${projectId}/agents/${agentId}/schema`,
  );
  return res.data;
}

export async function getPreparedInputs(
  projectId: string,
  agentId: string,
): Promise<PreparedInputsResponse> {
  const res = await apiClient.get<PreparedInputsResponse>(
    `/api/projects/${projectId}/agents/${agentId}/prepared-inputs`,
  );
  return res.data;
}

export async function getExecutions(
  projectId: string,
  agentId: string,
  options?: { slim?: boolean },
): Promise<AgentExecution[]> {
  // S-018: slim=true omite output_summary e input_config (campos JSONB pesados).
  // UIs que listam runs devem passar slim=true; fetch do output_summary fica
  // sob demanda via getExecution(executionId). Payload cai de ~10MB/run pra ~1KB.
  const params = options?.slim ? { slim: true } : undefined;
  const res = await apiClient.get<AgentExecution[]>(
    `/api/projects/${projectId}/agents/${agentId}/executions`,
    { params },
  );
  return res.data;
}

export async function getExecution(
  projectId: string,
  agentId: string,
  executionId: string,
): Promise<AgentExecution> {
  const res = await apiClient.get<AgentExecution>(
    `/api/projects/${projectId}/agents/${agentId}/executions/${executionId}`,
  );
  return res.data;
}

export async function getChatHistory(
  projectId: string,
  agentId: string,
  executionId?: string,
  limit = 50,
): Promise<ChatMessage[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (executionId) params.set("execution_id", executionId);
  const res = await apiClient.get<ChatMessage[]>(
    `/api/projects/${projectId}/agents/${agentId}/chat/history?${params}`,
  );
  return res.data;
}

export interface GetProjectCostsOptions {
  /** ISO date/datetime — escopa a consulta a partir daqui (ex.: início do mês
   * corrente, para comparar contra um cap que é mensal por definição). Sem
   * isto, o resumo é acumulado desde sempre — o que o Overview também lê. */
  dateFrom?: string;
  /** Pede a série diária (`by_day`). Opt-in porque custa duas consultas a
   * mais no `agents`, e só a tela de custo desenha o gráfico — a Overview e o
   * workbench leem este mesmo endpoint só pelo total e pelo modelo. Sem ela,
   * o campo vem **ausente** (não vazio: vazio significaria "medido, não há
   * histórico"). */
  includeDaily?: boolean;
}

export async function getProjectCosts(
  projectId: string,
  options?: GetProjectCostsOptions,
): Promise<CostSummary> {
  const params: Record<string, string> = {};
  if (options?.dateFrom) params.date_from = options.dateFrom;
  if (options?.includeDaily) params.include_daily = "true";
  const res = await apiClient.get<CostSummary>(`/api/projects/${projectId}/costs`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return res.data;
}

async function _downloadBomBlob(
  projectId: string,
  agentId: string,
  executionId: string,
  suffix: "xlsx" | "csv",
  mimeType: string,
): Promise<void> {
  const res = await apiClient.get<Blob>(
    `/api/projects/${projectId}/agents/${agentId}/executions/${executionId}/files/bom.${suffix}`,
    { responseType: "blob" },
  );
  const blob = new Blob([res.data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `functional_bom_${executionId}.${suffix}`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export async function downloadBomFile(
  projectId: string,
  agentId: string,
  executionId: string,
): Promise<void> {
  return _downloadBomBlob(
    projectId,
    agentId,
    executionId,
    "xlsx",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  );
}

export async function downloadBomCsv(
  projectId: string,
  agentId: string,
  executionId: string,
): Promise<void> {
  return _downloadBomBlob(
    projectId,
    agentId,
    executionId,
    "csv",
    "text/csv;charset=utf-8",
  );
}

// ---------------------------------------------------------------------------
// SSE Streaming — uses native fetch (not axios) for POST + SSE
// ---------------------------------------------------------------------------

export interface SSECallbacks {
  onEvent: (event: StreamEventType, data: unknown) => void;
  onError?: (error: Error) => void;
  onDone?: () => void;
}

type StreamOutcome = "complete" | "error" | "cancelled";
type RequestSpan = ReturnType<typeof Sentry.startInactiveSpan>;

interface SseRequestOptions<TRequest> {
  path: string;
  request: TRequest;
  callbacks: SSECallbacks;
  agentId: string;
  projectId: string;
  spanName: string;
  spanOp: string;
  streamKind: "execution" | "chat" | "resume";
  durationMetricName: string;
}

interface SseParserState {
  buffer: string;
  currentEvent: string;
  eventCount: number;
  malformedPayloadCount: number;
}

interface SseResult {
  status: number;
  eventCount: number;
  malformedPayloadCount: number;
}

function hasSentry(): boolean {
  return !!import.meta.env.VITE_SENTRY_DSN;
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function createStreamSpan(
  options: Pick<SseRequestOptions<unknown>, "path" | "spanName" | "spanOp" | "streamKind" | "agentId" | "projectId">,
): RequestSpan | undefined {
  if (!hasSentry()) return undefined;

  return Sentry.startInactiveSpan({
    name: options.spanName,
    op: options.spanOp,
    attributes: {
      "http.request.method": "POST",
      "http.route": options.path,
      "stream.kind": options.streamKind,
      "agent.id": options.agentId,
      "project.id": options.projectId,
    },
  });
}

function captureStreamDuration(
  options: Pick<SseRequestOptions<unknown>, "agentId" | "projectId" | "streamKind" | "durationMetricName">,
  startTime: number,
  outcome: StreamOutcome,
  result?: Partial<SseResult>,
): void {
  telemetry.captureMetric(options.durationMetricName, Date.now() - startTime, {
    agentId: options.agentId,
    projectId: options.projectId,
    stream: options.streamKind,
    outcome,
    status: String(result?.status ?? "unknown"),
    event_count: result?.eventCount ?? 0,
    malformed_payload_count: result?.malformedPayloadCount ?? 0,
  });
}

function finalizeStreamSpan(
  span: RequestSpan | undefined,
  options: Pick<SseRequestOptions<unknown>, "agentId" | "projectId" | "streamKind">,
  outcome: StreamOutcome,
  result?: Partial<SseResult>,
  error?: Error,
): void {
  if (!span) return;

  span.setAttributes({
    "agent.id": options.agentId,
    "project.id": options.projectId,
    "stream.kind": options.streamKind,
    "stream.outcome": outcome,
    "stream.event_count": result?.eventCount ?? 0,
    "stream.malformed_payload_count": result?.malformedPayloadCount ?? 0,
    ...(result?.status != null ? { "http.response.status_code": result.status } : {}),
  });

  if (result?.status != null) {
    Sentry.setHttpStatus(span, result.status);
  }

  if (outcome === "error") {
    span.setStatus({ code: 2, message: error?.message ?? "stream_failed" });
    span.setAttribute("error", true);
  } else if (outcome === "cancelled") {
    span.setStatus({ code: 1, message: "cancelled" });
  } else {
    span.setStatus({ code: 1 });
  }

  span.end();
}

function processSseChunk(
  chunk: string,
  state: SseParserState,
  options: Pick<SseRequestOptions<unknown>, "agentId" | "projectId" | "streamKind" | "callbacks" | "path">,
): void {
  // Varre o CHUNK, nunca o buffer acumulado.
  //
  // A versão original fazia `buffer.split("\n")` a cada chunk, o que é O(n²)
  // no tamanho da mensagem. O evento terminal do Functional carrega o
  // `outputs` inteiro e chega a 13 MB (medido na execução `dd46a665`, dos
  // quais 12 MB são `extraction_summary`): em 16 KB por chunk são ~800
  // refatiamentos de uma string que cresce até 13 MB, com a thread de UI
  // travada. Medido: 1 MB custava 0,2s, 4 MB 2,8s, 8 MB 11,9s, 13 MB 25,5s.
  //
  // Varrer o buffer a partir de um índice também não resolve, e essa foi a
  // primeira tentativa: `buffer += chunk` monta uma cons-string, e qualquer
  // `indexOf` sobre ela força achatar os 13 MB de novo a cada chunk. O custo
  // continuava quadrático (medido 4,4x ao dobrar o payload, contra 4,5x do
  // original).
  //
  // Aqui o buffer é só a cauda incompleta e ninguém a percorre: as quebras são
  // procuradas dentro do chunk, e a concatenação só é forçada a achatar quando
  // uma linha de fato fecha — uma vez por linha, não uma vez por chunk. Ver #834.
  const lines: string[] = [];
  let lineStart = 0;
  let newlineAt = chunk.indexOf("\n");

  if (newlineAt === -1) {
    state.buffer += chunk;
  } else {
    // A primeira linha fecha o que estava pendente na cauda.
    lines.push(state.buffer + chunk.slice(0, newlineAt));
    lineStart = newlineAt + 1;

    newlineAt = chunk.indexOf("\n", lineStart);
    while (newlineAt !== -1) {
      lines.push(chunk.slice(lineStart, newlineAt));
      lineStart = newlineAt + 1;
      newlineAt = chunk.indexOf("\n", lineStart);
    }
    state.buffer = chunk.slice(lineStart);
  }

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    if (!line) {
      state.currentEvent = "";
      continue;
    }

    if (line.startsWith("event:")) {
      state.currentEvent = line.slice(6).trim();
      continue;
    }

    if (!line.startsWith("data:")) {
      continue;
    }

    const dataStr = line.slice(5).trim();
    if (!dataStr) {
      state.currentEvent = "";
      continue;
    }

    try {
      const data = JSON.parse(dataStr);
      const eventType = (state.currentEvent || "token") as StreamEventType;
      state.eventCount += 1;
      options.callbacks.onEvent(eventType, data);
    } catch {
      state.malformedPayloadCount += 1;
      telemetry.captureWarning("sse", "Malformed SSE payload", {
        raw: dataStr,
        agentId: options.agentId,
        projectId: options.projectId,
        stream: options.streamKind,
        endpoint: options.path,
      });
    }

    state.currentEvent = "";
  }
}

async function readSseResponse(
  response: Response,
  options: Pick<SseRequestOptions<unknown>, "agentId" | "projectId" | "streamKind" | "callbacks" | "path">,
): Promise<SseResult> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  const state: SseParserState = {
    buffer: "",
    currentEvent: "",
    eventCount: 0,
    malformedPayloadCount: 0,
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    processSseChunk(decoder.decode(value, { stream: true }), state, options);
  }

  const trailingText = state.buffer + decoder.decode();
  if (trailingText.trim()) {
    state.buffer = "";
    processSseChunk(`${trailingText}\n`, state, options);
  }

  return {
    status: response.status,
    eventCount: state.eventCount,
    malformedPayloadCount: state.malformedPayloadCount,
  };
}

function executeSseRequest<TRequest>(options: SseRequestOptions<TRequest>): AbortController {
  const controller = new AbortController();
  const startTime = Date.now();
  const span = createStreamSpan(options);

  void fetch(options.path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(options.request),
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        const text = await response.text();
        let code: string | undefined;
        try {
          const detail = JSON.parse(text)?.detail;
          if (typeof detail?.code === "string") code = detail.code;
        } catch { /* Respostas não JSON preservam o erro de transporte. */ }
        const failure = Object.assign(
          new Error(`Stream failed (${response.status}): ${text}`),
          { status: response.status, code },
        );
        throw failure;
      }

      const result = await readSseResponse(response, options);
      captureStreamDuration(options, startTime, "complete", result);
      finalizeStreamSpan(span, options, "complete", result);
      options.callbacks.onDone?.();
    })
    .catch((error) => {
      const normalizedError = toError(error);
      const isAborted = normalizedError.name === "AbortError";
      const status = typeof (error as { status?: unknown })?.status === "number"
        ? (error as { status: number }).status
        : undefined;
      const result = status != null ? { status } : undefined;

      if (isAborted) {
        captureStreamDuration(options, startTime, "cancelled");
        finalizeStreamSpan(span, options, "cancelled");
        return;
      }

      captureStreamDuration(options, startTime, "error", result);
      finalizeStreamSpan(span, options, "error", result, normalizedError);
      telemetry.captureError("sse", normalizedError, {
        projectId: options.projectId,
        agentId: options.agentId,
        stream: options.streamKind,
        endpoint: options.path,
      });
      options.callbacks.onError?.(normalizedError);
    });

  return controller;
}

/**
 * Stream an agent execution via SSE. Returns an AbortController to cancel.
 */
export function streamAgent(
  projectId: string,
  agentId: string,
  request: AgentStreamRequest,
  callbacks: SSECallbacks,
): AbortController {
  return executeSseRequest({
    path: `/api/projects/${projectId}/agents/${agentId}/stream`,
    request,
    callbacks,
    agentId,
    projectId,
    spanName: "agent.stream",
    spanOp: "agent.execution",
    streamKind: "execution",
    durationMetricName: "agent.stream.duration",
  });
}

/**
 * Resume a paused agent execution via SSE (S-021 HITL-2/3).
 *
 * Forwards the human's response to `Command(resume=...)` on the
 * paused graph and streams the continuation. Returns an AbortController
 * so callers can cancel mid-resume.
 */
export function resumeAgent(
  projectId: string,
  agentId: string,
  executionId: string,
  request: AgentResumeRequest,
  callbacks: SSECallbacks,
): AbortController {
  return executeSseRequest({
    path: `/api/projects/${projectId}/agents/${agentId}/executions/${executionId}/resume`,
    request,
    callbacks,
    agentId,
    projectId,
    spanName: "agent.resume",
    spanOp: "agent.execution",
    streamKind: "resume",
    durationMetricName: "agent.resume.duration",
  });
}

/**
 * Chat with an agent via SSE. Returns an AbortController to cancel.
 */
export function chatWithAgent(
  projectId: string,
  agentId: string,
  request: AgentChatRequest,
  callbacks: SSECallbacks,
): AbortController {
  return executeSseRequest({
    path: `/api/projects/${projectId}/agents/${agentId}/chat`,
    request,
    callbacks,
    agentId,
    projectId,
    spanName: "agent.chat",
    spanOp: "agent.chat",
    streamKind: "chat",
    durationMetricName: "agent.chat.duration",
  });
}

export interface FunctionalDeliveryStatus {
  /** `null` quando o projeto ainda não aprovou: ausência declarada. */
  status: "pending" | "running" | "done" | "failed" | null;
  attempts: number;
  approval_version: number | null;
  /** Causa em vocabulário estável, quando a entrega falhou. */
  failure_code: string | null;
  /** A mesma causa em texto de usuário. O erro cru fica no log do serviço. */
  failure_message: string | null;
}

/**
 * Estado da entrega da aprovação mais recente (#854).
 *
 * A entrega roda num worker, fora da sessão: é por aqui que a tela sabe que ela
 * começou, terminou ou morreu, em vez de ficar muda até alguém recarregar.
 */
export async function getFunctionalDeliveryStatus(
  projectId: string,
): Promise<FunctionalDeliveryStatus> {
  const res = await apiClient.get<FunctionalDeliveryStatus>(
    `/api/projects/${projectId}/agents/functional/delivery`,
  );
  return res.data;
}

/** Recorte da listagem. Fica aqui, e não em `types/api.ts`, pelo mesmo motivo
 *  que `GetProjectCostsOptions`: é vocabulário do cliente (camelCase, opcional
 *  em tudo), não o contrato que o backend publica. */
export interface GetProjectRunsOptions {
  status?: string;
  agentType?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  /** Só as runs que custaram algo — o que o card de cobranças da tela de
   *  custo precisa. O corte é no banco, sobre o projeto inteiro. */
  hasCost?: boolean;
  page?: number;
  limit?: number;
}

/** Histórico de execução do projeto, de todos os agentes (#867).
 *
 * Filtro vazio não entra no fio: mandar `status=` ou `q=` em branco faria o
 * backend montar cláusula para "qualquer coisa", e a consulta continuaria
 * respondendo — barato de errar, caro de enxergar depois.
 */
export async function getProjectRuns(
  projectId: string,
  options?: GetProjectRunsOptions,
): Promise<ProjectRunsResponse> {
  const params: Record<string, string | number | boolean> = {};
  const text = (value?: string) => value?.trim() || undefined;

  const status = text(options?.status);
  if (status) params.status = status;
  const agentType = text(options?.agentType);
  if (agentType) params.agent_type = agentType;
  const query = text(options?.q);
  if (query) params.q = query;
  const dateFrom = text(options?.dateFrom);
  if (dateFrom) params.date_from = dateFrom;
  const dateTo = text(options?.dateTo);
  if (dateTo) params.date_to = dateTo;
  // Só `true` viaja: `false` não restringe nada, e é a mesma regra dos outros
  // filtros — o que não recorta não vai no fio.
  if (options?.hasCost) params.has_cost = true;
  if (options?.page != null) params.page = options.page;
  if (options?.limit != null) params.limit = options.limit;

  const res = await apiClient.get<ProjectRunsResponse>(
    `/api/projects/${projectId}/runs`,
    Object.keys(params).length > 0 ? { params } : undefined,
  );
  return res.data;
}

/** Agregados do hero da tela de Runs. Sem recorte: descrevem o projeto
 *  inteiro, e a janela entra como parâmetro no dia em que a tela tiver o
 *  controle para escolhê-la. */
export async function getProjectRunStats(
  projectId: string,
): Promise<ProjectRunStats> {
  const res = await apiClient.get<ProjectRunStats>(
    `/api/projects/${projectId}/runs/stats`,
  );
  return res.data;
}
